import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { CallBar, ChatThread, useVoiceCall, type CallApi, type CallInfo, type ChatMessage } from '@ck/ui';
import { callRelayUrl, type PublicTenant } from '@ck/sdk';
import { API_URL } from './api';

/* The chat bubble on the main website opens this page in a frame. Visitors chat without an account,
   with text and voice notes; chats land in the CRM support inbox. */
const KEY = 'ck.livechat';
type Saved = { id: string; token: string; name: string };
const load = (): Saved | null => { try { return JSON.parse(localStorage.getItem(KEY) ?? 'null'); } catch { return null; } };
const save = (v: Saved | null) => { try { if (v) localStorage.setItem(KEY, JSON.stringify(v)); else localStorage.removeItem(KEY); } catch { /* storage blocked: chat lasts this visit */ } };
const TENANT = import.meta.env.VITE_TENANT as string | undefined;

async function call<T>(path: string, init: RequestInit & { token?: string } = {}): Promise<T> {
  const headers: Record<string, string> = { ...(init.headers as Record<string, string>) };
  if (TENANT) headers['x-ck-tenant'] = TENANT;
  if (init.token) headers['x-guest-token'] = init.token;
  if (init.body && !(init.body instanceof FormData)) headers['content-type'] = 'application/json';
  const res = await fetch(API_URL.replace(/\/$/, '') + path, { ...init, headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error((data as { message?: string }).message ?? 'Something went wrong. Try again.'), { status: res.status });
  return data as T;
}

export function LiveChat({ tenant }: { tenant: PublicTenant }) {
  const [me, setMe] = useState<Saved | null>(load);
  const close = () => window.parent !== window && window.parent.postMessage('ckchat:close', '*');
  return (
    <div className="lc">
      <header className="lc-top"><MessageCircle aria-hidden /><b>{tenant.branding.name} support<small>We usually reply within a few minutes</small></b><button aria-label="Close chat window" onClick={close}><X /></button></header>
      <div className="lc-body">{me ? <Thread me={me} onEnd={() => { save(null); setMe(null); }} /> : <Start onStarted={(v) => { save(v); setMe(v); }} />}</div>
    </div>
  );
}

function Start({ onStarted }: { onStarted: (v: Saved) => void }) {
  const [f, setF] = useState({ name: '', email: '', topic: 'support', message: '' });
  const [topics, setTopics] = useState<{ id: string; name: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { call<{ topics: { id: string; name: string }[] }>('/v1/livechat/topics').then((d) => setTopics(d.topics)).catch(() => undefined); }, []);
  async function go(e: FormEvent) {
    e.preventDefault(); setBusy(true); setErr('');
    try { const d = await call<{ id: string; token: string }>('/v1/livechat/start', { method: 'POST', body: JSON.stringify(f) }); onStarted({ id: d.id, token: d.token, name: f.name }); }
    catch (x) { setErr((x as Error).message); } finally { setBusy(false); }
  }
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  return (
    <form className="lc-start form" onSubmit={go}>
      <p>Ask us anything about challenges, payouts or your account. You can type or send a voice note once the chat starts.</p>
      {err && <p className="msg err" role="alert">{err}</p>}
      <label>Your name<input required minLength={2} autoComplete="name" value={f.name} onChange={set('name')} /></label>
      <label>Email <small>for replies if you leave</small><input type="email" required autoComplete="email" value={f.email} onChange={set('email')} /></label>
      {topics.length > 0 && <label>Topic<select value={f.topic} onChange={set('topic')}>{topics.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></label>}
      <label>Your question<textarea required rows={3} value={f.message} onChange={set('message')} /></label>
      <button className="btn" disabled={busy}>{busy ? 'Starting…' : 'Start chat'}</button>
    </form>
  );
}

function Thread({ me, onEnd }: { me: Saved; onEnd: () => void }) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [typing, setTypingText] = useState<string | null>(null);
  const [status, setStatus] = useState('waiting');
  const [err, setErr] = useState('');
  const base = `/v1/livechat/${me.id}`;
  const refresh = useCallback(async () => {
    try { const d = await call<{ messages: ChatMessage[]; chat: { status: string }; typing?: string | null }>(base, { token: me.token }); setMessages(d.messages); setTypingText(d.typing ?? null); setStatus(d.chat.status); setErr(''); }
    catch (x) { if ((x as { status?: number }).status === 404 || (x as { status?: number }).status === 401) onEnd(); else setErr((x as Error).message); }
  }, [base, me.token]);
  useEffect(() => { void refresh(); const t = setInterval(() => { if (!document.hidden) void refresh(); }, 4000); return () => clearInterval(t); }, [refresh]);
  async function send(text: string, file: File | null) {
    try {
      if (file) { const form = new FormData(); form.append('body', text); form.append('file', file); await call(`${base}/files`, { method: 'POST', body: form, token: me.token }); }
      else await call(`${base}/messages`, { method: 'POST', body: JSON.stringify({ body: text }), token: me.token });
      setErr(''); await refresh(); return true;
    } catch (x) { setErr((x as Error).message); return false; }
  }
  async function loadFile(mid: string) {
    const headers: Record<string, string> = { 'x-guest-token': me.token };
    if (TENANT) headers['x-ck-tenant'] = TENANT;
    const res = await fetch(`${API_URL.replace(/\/$/, '')}${base}/files/${mid}`, { headers });
    if (!res.ok) throw new Error('Could not load the file.');
    return URL.createObjectURL(await res.blob());
  }
  const closed = status === 'closed';
  // Calls use the same support call service as the client portal, with the chat token instead of a sign-in.
  const callApi: CallApi = useMemo(() => {
    const S = '/v1/support', t = me.token;
    const post = <T,>(path: string, body: unknown) => call<T>(path, { method: 'POST', body: JSON.stringify(body), token: t });
    return {
      side: 'client',
      relayUrl: (cid) => callRelayUrl(API_URL, cid, 'client', t),
      iceServers: () => call<{ iceServers: RTCIceServer[] }>(`${S}/ice-servers`).then((r) => r.iceServers),
      current: () => call<{ call: CallInfo | null }>(`${S}/chats/${me.id}/call`, { token: t }).then((r) => r.call),
      get: (cid) => call<{ call: CallInfo }>(`${S}/calls/${cid}`, { token: t }).then((r) => r.call),
      start: (offer) => post<{ id: string }>(`${S}/chats/${me.id}/call`, { offer }),
      answer: (cid, sdp) => post(`${S}/calls/${cid}/answer`, { answer: sdp }),
      ice: (cid, candidate) => post(`${S}/calls/${cid}/ice`, { candidate }),
      end: (cid) => post(`${S}/calls/${cid}/end`, {}),
      diag: (cid, events) => post(`${S}/calls/${cid}/diag`, { events }),
    };
  }, [me.id, me.token]);
  const voice = useVoiceCall(callApi, !closed);
  return (
    <>
      {!closed && <div className="lc-call"><CallBar v={voice} who="support" /></div>}
      {err && <p className="msg err" role="alert" style={{ margin: 8 }}>{err}</p>}
      <ChatThread messages={messages} side="client" onSend={send} typing={typing} loadFile={loadFile} disabled={closed} disabledText="This chat has ended. Thanks for talking to us." placeholder="Type, or tap the mic for a voice note" />
      <div className="lc-end">
        <span>Chatting as {me.name}</span>
        {closed ? <button className="link" onClick={onEnd}>Start a new chat</button>
          : <button className="link" onClick={async () => { await call(`${base}/close`, { method: 'POST', token: me.token }).catch(() => undefined); await refresh(); }}>End chat</button>}
      </div>
    </>
  );
}
